import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useOptions } from './hooks';
import { Input, Radio, Tabs } from 'udesk-ui';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ConfigMode, RuleTypeMap } from './const';
import { getItemStyle, getListDropStyle, getListStyle, insert, reorder } from './utils';

export interface TemplateProps {
    value?: {
        formula?: string;
        judgeStrategy?: number;
    }[];
    onChange?: (v?: TemplateProps['value']) => void;
    operatorList?: any[];
}

const Template: FC<TemplateProps> = memo((props) => {
    const { value, onChange, operatorList } = props;

    const { options } = useOptions();

    /** 规则类型 */
    const [ruleType, setRuleType] = useState<number>();

    /** 配置的规则 - 用于手动配置的展示，以及传给后端 */
    const [operatorLogic, setOperatorLogic] = useState<string>();

    /** 算子列表 */
    const [conditionListFilter, setConditionListFilter] = useState<any[]>([]);

    /** 运算符列表 */
    const logicList = useMemo(
        () => [
            {
                id: 'logicList-1',
                content: UdeskLocales['current'].components.common.customFilter.and,
                value: 'and',
                flag: true,
            },
            {
                id: 'logicList-2',
                content: UdeskLocales['current'].components.common.customFilter.or,
                value: 'or',
                flag: true,
            },
            {
                id: 'logicList-3',
                content: UdeskLocales['current'].components.common.customFilter.not,
                value: 'not',
                flag: true,
            },
            {
                id: 'logicList-4',
                content: '(',
                value: '(',
                flag: true,
            },
            {
                id: 'logicList-5',
                content: ')',
                value: ')',
                flag: true,
            },
            {
                id: 'logicList-6',
                content: UdeskLocales['current'].components.common.customFilter.and,
                value: '&&',
                flag: false,
            },
            {
                id: 'logicList-7',
                content: UdeskLocales['current'].components.common.customFilter.or,
                value: '||',
                flag: false,
            },
            {
                id: 'logicList-8',
                content: UdeskLocales['current'].components.common.customFilter.not,
                value: '!',
                flag: false,
            },
        ],
        []
    );

    /** 配置的规则 - 用于拖拽配置的展示 */
    const [items, setItems] = useState<any[]>([]);

    /** 用于拖拽配置元数据的id计数 */
    const [itemsCount, setItemsCount] = useState(0);

    useEffect(() => {
        const judgeStrategy = value?.[0].judgeStrategy;
        const formula = value?.[0].formula;

        setRuleType(judgeStrategy);
        setOperatorLogic(formula);

        if (judgeStrategy === RuleTypeMap.CUSTOMIZE) {
            setItems([]);

            let list = formula?.split(/(or|and|not|&&|\|\||!|\(|\))/);

            setItemsCount(list?.length ?? 0);

            list?.forEach((item, index) => {
                item = item.trim();
                if (item && item.length > 0) {
                    const condition = conditionListFilter.find((current) => current.value === item);
                    const logic = logicList.find((current) => current.value === item);

                    if (condition) {
                        setItems((items) => [
                            ...items,
                            {
                                id: `drag-${index}`,
                                content: condition.content,
                                value: condition.value,
                                prevId: condition.id,
                                prevClass: condition.id.split('-')[0],
                            },
                        ]);
                    } else if (logic) {
                        setItems((items) => [
                            ...items,
                            {
                                id: `drag-${index}`,
                                content: logic.content,
                                value: logic.value,
                                prevId: logic.id,
                                prevClass: logic.id.split('-')[0],
                            },
                        ]);
                    }
                }
            });
        }
    }, [conditionListFilter, logicList, value]);

    const getOperatorList = useCallback(() => {
        if (operatorList?.length) {
            setConditionListFilter(
                operatorList.map((item, index) => {
                    return {
                        id: `conditionListFilter-${item.idCode}`,
                        content: item.idCode,
                        value: item.idCode,
                    };
                })
            );
            setItems((items) =>
                items
                    .filter(
                        (current) => current.prevId !== `conditionListFilter-${operatorList.length}`
                    )
                    .map((item, index) => {
                        return {
                            ...item,
                            id: `drag-${index}`,
                        };
                    })
            );
        }
    }, [operatorList]);

    useEffect(() => {
        getOperatorList();
    }, [getOperatorList]);

    const onGradeTypeChange = (v: number) => {
        setItems([]);
        setItemsCount(0);
        setRuleType(v);

        onChange?.(
            value?.map((item) => {
                return {
                    ...item,
                    formula: '',
                    judgeStrategy: v,
                };
            })
        );
    };

    const customJudgeLogicChange = (v: string) => {
        const reg = /[\u4e00-\u9fa5]/g;
        const str = v.replace(reg, '');
        setOperatorLogic(str);
        onChange?.(
            value?.map((item) => {
                return {
                    ...item,
                    formula: str,
                };
            })
        );
        setItems([]);
    };

    const [tarskDel, setTarskDel] = useState(false);

    const onDragStart = (option) => {
        if (option.draggableId.indexOf('drag') !== -1) {
            setTarskDel(true);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            if (result.draggableId.indexOf('drag') !== -1) {
                let list = items.filter((current) => current.id !== result.draggableId);
                setItems(list);

                let str = '';
                list.forEach((item) => {
                    item && (str += item.value);
                });
                setOperatorLogic(str);
                onChange?.(
                    value?.map((item) => {
                        return {
                            ...item,
                            formula: str,
                        };
                    })
                );
                setTarskDel(false);
            }
            return;
        }

        if (result.draggableId.indexOf('drag') !== -1) {
            const _items = reorder(items, result.source.index, result.destination.index) as any[];
            let str = '';
            _items.forEach((item) => {
                item && (str += item.value);
            });
            setOperatorLogic(str);
            onChange?.(
                value?.map((item) => {
                    return {
                        ...item,
                        formula: str,
                    };
                })
            );
            setItems(_items);
            setTarskDel(false);
        } else {
            if (result.draggableId.indexOf('-') !== 1) {
                let drag = result.draggableId.split('-')[0];

                let obj: any = undefined;

                if (drag === 'conditionListFilter') {
                    obj = conditionListFilter.find((current) => current.id === result.draggableId);
                } else if (drag === 'logicList') {
                    obj = logicList.find((current) => current.id === result.draggableId);
                }

                if (obj) {
                    let data = {
                        id: `drag-${itemsCount + 1}`,
                        content: obj.content,
                        value: obj.value,
                        prevId: result.draggableId,
                        prevClass: drag,
                    };
                    const _items = insert(items, data, result.destination.index) as any[];
                    let str = '';
                    _items.forEach((item) => {
                        item && (str += item.value);
                    });
                    setOperatorLogic(str);
                    onChange?.(
                        value?.map((item) => {
                            return {
                                ...item,
                                formula: str,
                            };
                        })
                    );
                    setItemsCount(itemsCount + 1);
                    setItems(_items);
                }
            }
        }
    };

    return (
        <div className="tasks-manage-template-configuration-detail-logic-rule">
            <Radio.Group onChange={(e) => onGradeTypeChange(e.target.value)} value={ruleType}>
                {options.map((o) => (
                    <Radio value={o.value}>{o.label}</Radio>
                ))}
            </Radio.Group>

            {ruleType === RuleTypeMap.CUSTOMIZE ? (
                <Tabs defaultActiveKey={ConfigMode.DRAG}>
                    <Tabs.TabPane
                        tab={
                            UdeskLocales['current'].components.common.customFilter
                                .manualConfiguration
                        }
                        key={ConfigMode.MANUL}
                    >
                        <Input
                            placeholder={
                                UdeskLocales['current'].components.common.customFilter
                                    .holderCustomRule
                            }
                            value={operatorLogic}
                            onChange={(e) => customJudgeLogicChange(e.target.value)}
                        />
                    </Tabs.TabPane>

                    <Tabs.TabPane
                        tab={
                            UdeskLocales['current'].components.common.customFilter.dragConfiguration
                        }
                        key={ConfigMode.DRAG}
                    >
                        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                            <div className="data-preprocess-custom">
                                <div className="data-preprocess-custom-drap-title">
                                    {
                                        UdeskLocales['current'].components.common.customFilter
                                            .condition
                                    }
                                    ：
                                </div>
                                <div className="data-preprocess-cutom-drap-info">
                                    <Droppable
                                        droppableId="droppables"
                                        direction="horizontal"
                                        isDropDisabled={true}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                style={getListStyle()}
                                            >
                                                {conditionListFilter.map((item, index) => (
                                                    <Draggable
                                                        key={item.id}
                                                        draggableId={item.id}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                style={getItemStyle(
                                                                    snapshot.isDragging,
                                                                    provided.draggableProps.style
                                                                )}
                                                            >
                                                                {item.content}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                            <div className="data-preprocess-custom">
                                <div className="data-preprocess-custom-drap-title">运算：</div>
                                <div className="data-preprocess-cutom-drap-info">
                                    <Droppable
                                        droppableId="droppabless"
                                        direction="horizontal"
                                        isDropDisabled={true}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                style={getListStyle()}
                                            >
                                                {logicList
                                                    .filter((item) => item.flag)
                                                    .map((item, index) => (
                                                        <Draggable
                                                            key={item.id}
                                                            draggableId={item.id}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getItemStyle(
                                                                        snapshot.isDragging,
                                                                        provided.draggableProps
                                                                            .style
                                                                    )}
                                                                >
                                                                    {item.content}
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                            <div className="data-preprocess-custom">
                                {tarskDel && (
                                    <div className="index-manmagement-custom-drag-tarsk">
                                        {
                                            UdeskLocales['current'].components.common.customFilter
                                                .infoDel
                                        }
                                    </div>
                                )}
                                <div className="data-preprocess-custom-drap-title">
                                    {
                                        UdeskLocales['current'].components.common.customFilter
                                            .toConfigure
                                    }
                                    ：
                                </div>
                                <div className="data-preprocess-cutom-drap-info">
                                    <Droppable droppableId="droppable" direction="horizontal">
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                style={getListDropStyle()}
                                            >
                                                {items.map((item, index) => {
                                                    return (
                                                        <Draggable
                                                            key={item.id}
                                                            draggableId={item.id}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getItemStyle(
                                                                        snapshot.isDragging,
                                                                        provided.draggableProps
                                                                            .style
                                                                    )}
                                                                >
                                                                    {item.content}
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                        </DragDropContext>
                    </Tabs.TabPane>
                </Tabs>
            ) : null}
        </div>
    );
});

export default Template;
